import React from 'react';
import { Loader2, ArrowRight } from 'lucide-react';
import {
  isValidIndianMobile, isPartialIndianMobile,
  normalizeIndianMobile, phoneErrorMessage,
} from '../../utils/phone';

export const BOOKING_RELATIONSHIPS = [
  'self', 'spouse', 'son', 'daughter', 'father', 'mother', 'brother', 'sister', 'guardian', 'other',
];

export const NON_DEPENDENT_AFFILIATIONS = [
  { value: 'faculty_staff_relative', label: 'Relative to any faculty or staff' },
  { value: 'guest', label: 'Guest' },
  { value: 'project_staff', label: 'Project Staff' },
  { value: 'intern', label: 'Interns' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'other', label: 'Other' },
];

export function BookingDateStep({ dates, datesWithSlots, datesFullyBooked, pickedDate, primary, onPick, onBack }) {
  const fullSet = datesFullyBooked || new Set();
  return (
    <div data-testid="booking-date-step">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-zinc-400">Pick a date</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Grey days have no open slots · then pick a time below</p>
        </div>
        <button type="button" onClick={onBack} className="text-[11px] text-zinc-500 hover:text-zinc-300">
          Change doctor
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="booking-dates">
        {dates.map((d) => {
          const hasOpen = datesWithSlots.has(d.iso);
          const full = fullSet.has(d.iso);
          const isPicked = pickedDate === d.iso;
          return (
            <button
              key={d.iso}
              type="button"
              disabled={!hasOpen}
              onClick={() => onPick(d.iso)}
              className={`flex-shrink-0 px-3 py-2 rounded-lg border text-xs transition relative ${
                isPicked ? 'text-white' :
                hasOpen ? 'border-white/15 text-zinc-200 hover:bg-white/10' :
                'border-white/5 text-zinc-600 cursor-not-allowed opacity-60'
              }`}
              style={isPicked ? { background: primary, borderColor: primary } : {}}
              data-testid={`booking-date-${d.iso}`}
            >
              <div className="text-[10px] uppercase opacity-80">{d.weekday}</div>
              <div className="text-base font-bold">{d.day}</div>
              <div className="text-[9px] opacity-70">{d.month}</div>
              {full && !hasOpen && (
                <span className="absolute top-0.5 right-0.5 text-[7px] uppercase text-rose-300">Full</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function BookingPatientForm({
  form, setForm, phoneTouched, setPhoneTouched, error, submitting, primary,
  onSubmit, onBack, showRelationship = false, relationshipMode = 'family',
  locked = false, submitLabel = 'Continue to email verification',
  emailPlaceholder = 'Email (for OTP)', contactNotice,
}) {
  const notice = contactNotice || (
    'Please ensure your email and phone number are correct — they will be used for future references '
    + '(appointment updates, OTP verification, and hospital follow-up).'
  );
  const phoneShowError = (phoneTouched && !isValidIndianMobile(form.phone))
    || (form.phone.length > 0 && !isPartialIndianMobile(form.phone));
  const phoneErrMsg = phoneShowError ? phoneErrorMessage(form.phone) : '';
  const isAffiliation = relationshipMode === 'affiliation';

  return (
    <div className="space-y-2.5 border-t border-white/10 pt-4 mt-4" data-testid="booking-form">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] uppercase tracking-wider text-zinc-400">Your details</p>
        <button type="button" onClick={onBack} className="text-[11px] text-zinc-500 hover:text-zinc-300">Back</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input placeholder="Full name" value={form.name} disabled={locked}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 disabled:opacity-70"
          data-testid="booking-name" />
        <input placeholder={emailPlaceholder} type="email" value={form.email} disabled={locked}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 disabled:opacity-70"
          data-testid="booking-email" />
      </div>
      <input placeholder="10-digit mobile" value={form.phone} inputMode="tel" maxLength={16} disabled={locked}
        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
        onBlur={() => setPhoneTouched(true)}
        className={`w-full bg-white/5 border rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 disabled:opacity-70 ${
          phoneShowError ? 'border-red-400/70' : 'border-white/15'
        }`}
        data-testid="booking-phone" />
      {phoneShowError && <span className="text-[11px] text-red-300" data-testid="booking-phone-error">{phoneErrMsg}</span>}
      <p className="text-[11px] text-zinc-400 leading-relaxed" data-testid="booking-contact-notice">
        {notice}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="Age" value={form.age} disabled={locked}
          onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))}
          className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 disabled:opacity-70"
          data-testid="booking-age" />
        <select value={form.gender} disabled={locked}
          onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
          className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-70"
          data-testid="booking-gender">
          <option value="" className="text-black">Gender (optional)</option>
          <option value="male" className="text-black">Male</option>
          <option value="female" className="text-black">Female</option>
          <option value="other" className="text-black">Other</option>
        </select>
      </div>
      {showRelationship && (
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-400 mb-1.5">
            {isAffiliation ? 'Affiliation' : 'Relationship'}
          </label>
          <select value={form.relationship}
            onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))}
            className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="booking-relationship">
            {isAffiliation ? (
              <>
                <option value="" className="text-black">Select affiliation</option>
                {NON_DEPENDENT_AFFILIATIONS.map((o) => (
                  <option key={o.value} value={o.value} className="text-black">{o.label}</option>
                ))}
              </>
            ) : (
              BOOKING_RELATIONSHIPS.map((r) => (
                <option key={r} value={r} className="text-black">{r}</option>
              ))
            )}
          </select>
        </div>
      )}
      <textarea placeholder="Briefly, what brings you in? (optional)" value={form.symptoms} rows={2}
        onChange={(e) => setForm((f) => ({ ...f, symptoms: e.target.value }))}
        className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 resize-none"
        data-testid="booking-symptoms" />
      <input type="text" tabIndex={-1} autoComplete="off" value={form.website}
        onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
      {error && <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-400/30 rounded p-2">{error}</div>}
      <button type="button" onClick={onSubmit} disabled={submitting}
        className="w-full mt-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-white disabled:opacity-60"
        style={{ background: primary }} data-testid="booking-submit">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        {submitLabel}
      </button>
    </div>
  );
}

export { isValidIndianMobile, normalizeIndianMobile, phoneErrorMessage };
